import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ArrowRight } from 'lucide-react';

const ItinerariesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const itineraries = [
    {
      id: 'royal-rajasthan',
      title: 'Royal Rajasthan Experience',
      destination: 'India',
      duration: '10 Days / 9 Nights',
      image: 'https://images.unsplash.com/photo-1770665567877-72ee8a7c9051?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHx1ZGFpcHVyJTIwbGFrZSUyMHBhbGFjZSUyMGx1eHVyeSUyMGhvdGVsJTIwaW5kaWF8ZW58MHx8fHwxNzczMTQ0NTU0fDA&ixlib=rb-4.1.0&q=85',
      description: 'Journey through India\'s royal heritage with palace stays, private cultural experiences, and exclusive access to historical monuments.',
      priceGuidance: 'From $15,000 per person',
    },
    {
      id: 'amalfi-escape',
      title: 'Amalfi Coast Escape',
      destination: 'Italy',
      duration: '7 Days / 6 Nights',
      image: 'https://images.unsplash.com/photo-1534445538923-ab3daf341b3c?q=80&w=2574&auto=format&fit=crop',
      description: 'Mediterranean luxury along the stunning Amalfi coastline. Private yacht experiences, Michelin-star dining, and cliffside villa stays.',
      priceGuidance: 'From $12,000 per person',
    },
    {
      id: 'african-safari',
      title: 'African Luxury Safari',
      destination: 'Tanzania & Kenya',
      duration: '12 Days / 11 Nights',
      image: 'https://images.unsplash.com/photo-1709402606682-400133d92ab2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB0cmF2ZWwlMjBzYWZhcmklMjBzZXJlbmdldGklMjBzdW5zZXQlMjBwcml2YXRlJTIwamVlcHxlbnwwfHx8fDE3NzMxNDQ1NTN8MA&ixlib=rb-4.1.0&q=85',
      description: 'Witness the Great Migration, luxury tented camps, private game drives, and exclusive wildlife encounters in East Africa.',
      priceGuidance: 'From $18,000 per person',
    },
    {
      id: 'japan-odyssey',
      title: 'Japan Cultural Odyssey',
      destination: 'Japan',
      duration: '14 Days / 13 Nights',
      image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2670&auto=format&fit=crop',
      description: 'Immerse in Japanese culture through ryokan stays, private tea ceremonies, geisha experiences, and culinary journeys.',
      priceGuidance: 'From $16,000 per person',
    },
    {
      id: 'maldives-retreat',
      title: 'Maldives Private Island Retreat',
      destination: 'Maldives',
      duration: '5 Days / 4 Nights',
      image: 'https://images.unsplash.com/photo-1620483829646-8fb1d8162108?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxtYWxkaXZlcyUyMGx1eHVyeSUyMHdhdGVyJTIwdmlsbGElMjBvY2VhbiUyMGFlcmlhbHxlbnwwfHx8fDE3NzMxNDQ1NTR8MA&ixlib=rb-4.1.0&q=85',
      description: 'Ultimate tropical escape with overwater villas, private beaches, underwater dining, and personalized spa experiences.',
      priceGuidance: 'From $10,000 per person',
    },
    {
      id: 'patagonia-adventure',
      title: 'Patagonia Wilderness Adventure',
      destination: 'Argentina & Chile',
      duration: '9 Days / 8 Nights',
      image: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?q=80&w=2532&auto=format&fit=crop',
      description: 'Explore pristine Patagonian landscapes with luxury lodges, glacier expeditions, and exclusive access to remote wilderness.',
      priceGuidance: 'From $14,000 per person',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="itineraries-page"
    >
      {/* Hero */}
      <section className="relative h-[50vh] md:h-[60vh] flex items-center justify-center overflow-hidden" data-testid="itineraries-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />

        <div className="relative z-10 container-padding text-center max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-accent mb-6"
          >
            Sample
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-heading text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-[0.9] text-primary"
            data-testid="itineraries-title"
          >
            Curated Journeys
          </motion.h1>
        </div>
      </section>

      {/* Itineraries Grid */}
      <section className="section-padding container-padding" data-testid="itineraries-grid-section">
        <div className="max-w-[1920px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {itineraries.map((itinerary, index) => (
              <motion.div
                key={itinerary.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                data-testid={`itinerary-card-${itinerary.id}`}
              >
                <Link to={`/itineraries/${itinerary.id}`} className="block group">
                  <div className="overflow-hidden aspect-[3/4] mb-6">
                    <img
                      src={itinerary.image}
                      alt={itinerary.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-4 text-muted-foreground text-xs">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-accent" />
                        <span>{itinerary.destination}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-accent" />
                        <span>{itinerary.duration}</span>
                      </div>
                    </div>

                    <h3 className="font-heading text-3xl font-light text-primary group-hover:text-accent transition-colors duration-300">
                      {itinerary.title}
                    </h3>

                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {itinerary.description}
                    </p>

                    <div className="flex items-center justify-between pt-4">
                      <p className="text-accent text-sm font-medium">
                        {itinerary.priceGuidance}
                      </p>
                      <div className="btn-secondary inline-flex items-center gap-2">
                        View Details <ArrowRight size={14} />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding container-padding bg-white/[0.02]" data-testid="itineraries-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-light text-primary mb-6">
              Create Your Bespoke Journey
            </h2>
            <p className="text-muted-foreground text-base md:text-lg mb-10 max-w-2xl mx-auto">
              These are sample itineraries. Every journey we create is uniquely tailored to your
              preferences, desires, and dreams.
            </p>
            <Link to="/concierge" className="btn-primary" data-testid="itineraries-cta-btn">
              Start Planning
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default ItinerariesPage;